"use client";

import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuditLogs, useAuditLogTypes, type AuditLogFilters } from '@/hooks/audit';
import { AuditLogFilters as AuditLogFiltersComponent } from '@/components/audit/audit-log-filters';
import { AuditLogTable } from '@/components/audit/audit-log-table';
import { AuditLogAnalytics } from '@/components/audit/audit-log-analytics';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { RefreshCw } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';

const AuditLogsSettings = () => {
  const { t } = useTranslation();
  const [filters, setFilters] = useState<AuditLogFilters>({
    page: 1,
    limit: 20,
    sortBy: 'timestamp',
    sortOrder: 'desc',
  });
  const [searchParams, setSearchParams] = useSearchParams();
  const currentTab = searchParams.get('tab') || 'logs';

  // hydrate filters from URL
  useEffect(() => {
    const page = Number(searchParams.get('page') || '1');
    const action = searchParams.get('action') || undefined;
    const status = (searchParams.get('status') as 'SUCCESS' | 'FAILURE' | null) || undefined;
    const resource = searchParams.get('resource') || undefined;
    const startDate = searchParams.get('startDate') || undefined;
    const endDate = searchParams.get('endDate') || undefined;
    setFilters(prev => ({
      ...prev,
      page: isNaN(page) ? 1 : page,
      action,
      status,
      resource,
      startDate,
      endDate,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { data: auditLogsData, isLoading, error, refetch } = useAuditLogs(filters);
  const [searchInput, setSearchInput] = useState<string>(searchParams.get('search') || '');

  // Debounce search input -> backend query param
  useEffect(() => {
    const t = setTimeout(() => {
      const next = new URLSearchParams(searchParams);
      const value = searchInput.trim();
      if (value) {
        next.set('search', value);
      } else {
        next.delete('search');
      }
      next.set('page', '1');
      setSearchParams(next, { replace: true });
      setFilters(prev => ({ ...prev, search: value || undefined, page: 1 }));
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);
  const { data: auditLogTypes } = useAuditLogTypes();

  const handleFiltersChange = (newFilters: Partial<AuditLogFilters>) => {
    setFilters(prev => ({ ...prev, ...newFilters, page: 1 }));
    const merged = { ...filters, ...newFilters, page: 1 };
    const next = new URLSearchParams(searchParams);
    next.set('page', String(merged.page));
    merged.action ? next.set('action', merged.action) : next.delete('action');
    merged.status ? next.set('status', merged.status) : next.delete('status');
    merged.resource ? next.set('resource', merged.resource) : next.delete('resource');
    merged.startDate ? next.set('startDate', merged.startDate) : next.delete('startDate');
    merged.endDate ? next.set('endDate', merged.endDate) : next.delete('endDate');
    setSearchParams(next, { replace: true });
  };

  const handlePageChange = (page: number) => {
    setFilters(prev => ({ ...prev, page }));
    const next = new URLSearchParams(searchParams);
    next.set('page', String(page));
    setSearchParams(next, { replace: true });
  };

  const handleRefresh = () => {
    refetch();
  };

  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-semibold">{t('auditLogs.title')}</h1>
        </div>
        <Card>
          <CardContent className="flex items-center justify-center h-64">
            <div className="text-center">
              <p className="text-red-500 mb-4">{t('auditLogs.errorLoading')}</p>
              <Button onClick={handleRefresh} variant="outline">
                <RefreshCw className="w-4 h-4 mr-2" />
                {t('common.retry')}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">{t('auditLogs.title')}</h1>
          <p className="text-muted-foreground mt-1">
            {t('auditLogs.description')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handleRefresh} disabled={isLoading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            {t('common.refresh')}
          </Button>
        </div>
      </div>

      <Tabs value={currentTab} onValueChange={(val) => {
        const next = new URLSearchParams(searchParams);
        next.set('tab', val);
        setSearchParams(next, { replace: true });
      }} className="space-y-6 w-full">
        <TabsList className="w-full">
          <TabsTrigger value="logs" className="flex-1">
            {t('auditLogs.logs')}
          </TabsTrigger>
          <TabsTrigger value="analytics" className="flex-1">
            {t('auditLogs.analytics')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="logs" className="space-y-6">
          <AuditLogFiltersComponent
            filters={filters}
            onFiltersChange={handleFiltersChange}
            auditLogTypes={auditLogTypes || []}
          />

          <AuditLogTable
            data={auditLogsData?.data || []}
            isLoading={isLoading}
            pagination={{
              currentPage: auditLogsData?.currentPage || 1,
              totalPages: auditLogsData?.totalPages || 1,
              totalItems: auditLogsData?.count || 0,
              onPageChange: handlePageChange,
            }}
            sortBy={filters.sortBy}
            sortOrder={filters.sortOrder}
            onSortChange={(sortBy, sortOrder) => {
              const normalizedSortBy = sortBy === 'user' ? 'userId' : sortBy;
              setFilters(prev => ({ ...prev, sortBy: normalizedSortBy, sortOrder, page: 1 }));
              const next = new URLSearchParams(searchParams);
              next.set('page', '1');
              normalizedSortBy ? next.set('sortBy', normalizedSortBy) : next.delete('sortBy');
              sortOrder ? next.set('sortOrder', sortOrder) : next.delete('sortOrder');
              setSearchParams(next, { replace: true });
            }}
            externalSearchValue={searchInput}
            onExternalSearchChange={setSearchInput}
          />
        </TabsContent>

        <TabsContent value="analytics">
          <AuditLogAnalytics filters={filters} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AuditLogsSettings;
